/* eslint-disable */
/**
 * COPIED from src/app/components/direct/data/serviceabilityUpload.js (commit 7772aebb).
 * Kept as close to byte-identical as possible so a two-way diff against the source stays
 * meaningful while both modules coexist. Ashvini's module is scheduled for removal; when it
 * goes, this copy becomes the only one and the eslint-disable above should be dropped.
 *
 * ONLY LOCAL CHANGE: `reason` is exported, so serviceabilityLanes.js can reuse it for the
 * list-mode branch instead of duplicating the rejection messages and letting them drift.
 */
/**
 * ProShip Direct — serviceability upload model.
 *
 * Reverse-engineered from the live admin implementation so the merchant-side flow speaks the
 * same contract rather than a parallel invention. Sources, for whoever picks this up:
 *
 *   admin/src/app/pages/bulk_serviceability/UploadWizard.js     — the 5-step pincode wizard
 *   admin/src/app/pages/bulk_serviceability/PdUploadWizard.js   — the SDD/NDD pair wizard
 *   admin/src/app/pages/bulk_serviceability/useBulkServiceability.js — all 11 endpoints
 *
 * Replaced by, one-for-one:
 *   GET    order/serviceability/parent-cps
 *   GET    order/serviceability/account-codes?parent_cp=
 *   POST   order/serviceability/upload?confirm=false          (dry run)
 *   POST   order/serviceability/upload?confirm=true           (fan-out commit)
 *   POST   order/serviceability/pd/upload?confirm=false|true   (SDD/NDD pairs)
 *   GET    order/serviceability/{accountCode}/status
 *   GET    order/serviceability/{accountCode}/history
 *   GET    order/serviceability/{accountCode}/download?pincodeType=
 *   GET    order/serviceability/audit-log
 *   PATCH  order/serviceability/{accountCode}/l1-toggle
 *   PATCH  order/serviceability/{accountCode}/l1-merchants
 *   DELETE order/serviceability/{accountCode}                 (PROSHIPADMIN only)
 *
 * PRODUCT DECISION, on Dr. Jakhar's instruction: there is deliberately **no** option to
 * inherit or "pull" Prozo's serviceability for the same carrier. A brand's Xpressbees
 * agreement does not cover the same pincodes as Prozo's Xpressbees agreement — coverage is a
 * property of the contract, not of the carrier's brand name. Offering a default would let a
 * merchant ship into lanes it has not bought, and the failure surfaces as an undeliverable
 * order days later. The merchant uploads, or the account stays unserviceable.
 */

// Exact admin values — these strings go on the wire.
export const PINCODE_TYPES = {
  PICKUP_PREPAID: 'PICKUP_PREPAID',
  DROP_PREPAID: 'DROP_PREPAID',
  DROP_COD: 'DROP_COD',
}

export const PINCODE_TYPE_LABELS = {
  PICKUP_PREPAID: 'Pickup Prepaid',
  DROP_PREPAID: 'Drop Prepaid',
  DROP_COD: 'Drop COD',
}

export const PINCODE_TYPE_HELPERS = {
  PICKUP_PREPAID: 'Pincodes from which this account can pick up',
  DROP_PREPAID: 'Delivery pincodes for prepaid orders',
  DROP_COD: 'Delivery pincodes for COD orders',
}

// PD = pickup→drop pair serviceability, which is how same-day and next-day are actually
// sold: a courier does not commit SDD "to Bangalore", it commits SDD from this pickup to
// that drop. Confirmed in scope for own couriers.
export const PD_SERVICE_TYPES = {SDD: 'SDD', NDD: 'NDD'}
export const PD_PAYMENT_MODES = {prepaid: 'prepaid', cod: 'cod'}

export const ACTION_TYPE_LABELS = {
  BULK_UPLOAD: 'Bulk Upload',
  PD_BULK_UPLOAD: 'PD Upload (SDD/NDD)',
  L1_TOGGLE: 'L1 Toggle',
  L1_SCOPE_UPDATE: 'L1 Scope Update',
  DEACTIVATE: 'Deactivate',
}

export const UPLOAD_STATUSES = {COMPLETED: 'COMPLETED', PARTIAL: 'PARTIAL', FAILED: 'FAILED'}

export const MAX_FILE_MB = 5

/**
 * A serviceable Indian pincode: six digits, first digit 1-8. 0 and 9 are unassigned, so a
 * row starting with either is a data-entry error rather than a remote location — worth
 * rejecting by rule instead of letting it fail at allocation.
 */
export const PINCODE_RE = /^[1-8]\d{5}$/

export const reason = (raw) => {
  const v = String(raw).trim()
  if (!v) return 'Empty'
  if (!/^\d+$/.test(v)) return 'Not numeric'
  if (v.length !== 6) return `${v.length} digits, expected 6`
  return 'Invalid pincode series'
}

/**
 * Client-side dry run over a real file, mirroring POST /upload?confirm=false.
 *
 * Deliberately parses the actual CSV the user picks rather than inventing a result: a
 * validation step that cannot fail teaches nothing, and the whole point of the dry run is
 * that the merchant sees their own bad rows before anything is committed.
 *
 * Expected shape, same as admin: a single column with header `pincode`.
 */
export const dryRunPincodes = (text) => {
  const rows = String(text).split(/\r?\n/).map((r) => r.split(',')[0])
  // Drop a header row if present, so a template download round-trips cleanly.
  if (rows.length && /pincode/i.test(rows[0])) rows.shift()

  const seen = new Set()
  const valid = []
  const invalidDetails = []
  let duplicatesRemoved = 0
  let totalRowsInFile = 0

  rows.forEach((raw, i) => {
    if (!String(raw).trim()) return
    totalRowsInFile += 1
    const v = String(raw).trim()
    if (!PINCODE_RE.test(v)) {
      // Cap the detail list: a 40k-row paste of garbage should not build a 40k-row array.
      if (invalidDetails.length < 50) invalidDetails.push({row: i + 2, value: v, reason: reason(v)})
      return
    }
    if (seen.has(v)) {
      duplicatesRemoved += 1
      return
    }
    seen.add(v)
    valid.push(v)
  })

  return {
    totalRowsInFile,
    validPincodes: valid.length,
    invalidRows: totalRowsInFile - valid.length - duplicatesRemoved,
    duplicatesRemoved,
    previewPincodes: valid.slice(0, 12),
    invalidDetails,
    errorCode: valid.length === 0 ? 'CSV_NO_VALID_PINCODES' : null,
  }
}

/** Dry run for the PD pair file: two columns, `pickup_pincode,drop_pincode`. */
export const dryRunPdPairs = (text) => {
  const rows = String(text).split(/\r?\n/)
  if (rows.length && /pickup/i.test(rows[0])) rows.shift()

  const pickups = new Set()
  const pairs = new Set()
  const invalidDetails = []
  let totalRowsInFile = 0

  rows.forEach((line, i) => {
    if (!String(line).trim()) return
    totalRowsInFile += 1
    const [p, d] = String(line).split(',').map((c) => String(c || '').trim())
    if (!PINCODE_RE.test(p) || !PINCODE_RE.test(d)) {
      if (invalidDetails.length < 50) {
        invalidDetails.push({
          row: i + 2,
          value: `${p || '—'} → ${d || '—'}`,
          reason: !PINCODE_RE.test(p) ? `Pickup: ${reason(p)}` : `Drop: ${reason(d)}`,
        })
      }
      return
    }
    pickups.add(p)
    pairs.add(`${p}|${d}`)
  })

  return {
    totalRowsInFile,
    pickupCount: pickups.size,
    totalPairs: pairs.size,
    invalidRows: invalidDetails.length,
    invalidDetails,
    previewPairs: [...pairs].slice(0, 10).map((k) => k.replace('|', ' → ')),
    errorCode: pairs.size === 0 ? 'CSV_NO_VALID_PINCODES' : null,
  }
}

/** The two templates, generated locally so Download Sample works with no backend. */
export const PINCODE_TEMPLATE = ['pincode', '110001', '400001', '560001', '700001'].join('\n')

export const PD_TEMPLATE = [
  'pickup_pincode,drop_pincode',
  '110001,110020',
  '110001,201301',
  '560001,560095',
].join('\n')

export const downloadCsv = (filename, body) => {
  const blob = new Blob([body], {type: 'text/csv;charset=utf-8;'})
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
